/* eslint-disable ember/no-computed-properties-in-native-classes */
import { concat } from '@ember/helper';
import { action, computed } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert } from '@ember/render-modifiers';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismSelectInput } from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import PowerCalendar from 'ember-power-calendar/components/power-calendar';
// eslint-disable-next-line import-x/no-named-as-default
import powerCalendarFormatDate from 'ember-power-calendar/helpers/power-calendar-format-date';
import { and } from 'ember-truth-helpers';
import { local } from 'embroider-css-modules';
import HotelMarketInsightCalendarsDaysDayMarketDemand from 'lumen/components/hotel/market-insight/calendars/days/day-market-demand';
import dayjsToDate from 'lumen/helpers/dayjs-to-date';
import scrollIntoView from 'lumen/modifiers/scroll-into-view';
import { CALENDAR_FILTERS } from 'lumen/utils/constants/market-insight-constants';

import styles from './sidebar-calendar.module.css';

// @ts-expect-error: Incorrect type
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function hasSameMonthAndYear(date1, date2) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
  return date1.isSame(date2, 'month');
}

interface HotelMarketInsightCalendarsSidebarCalendarSignature {
  Args: {
    hasReachedMainSidebarScrollThreshold: unknown;
    initPerfectScrollbarMainSidebar: unknown;
    isMarketInsightFull: unknown;
    mainSidebarWrapperId: unknown;
    model: unknown;
    screenHeight: unknown;
    selectedCalendarViewFilterIds: unknown;
    selectedRefDaysAgo: unknown;
    selectedStartDate: unknown;
    setSelectedCalendarViewFilterIds: unknown;
    sidebarCalendarRange: unknown;
    updateSelectedDay: unknown;
  };
}

export default class HotelMarketInsightCalendarsSidebarCalendar extends Component<HotelMarketInsightCalendarsSidebarCalendarSignature> {
  @service declare dates: Services['dates'];
  @service declare intl: Services['intl'];
  @service declare miSelections: Services['mi-selections'];
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  // Note: these options are not available for MI Light subscriptions, as they don't have any opportunities
  opportunityCalendarFilters = [
    {
      // @ts-expect-error: Incorrect type
      groupName: this.intl.t('market_insight.opportunities'),
      options: [
        {
          id: CALENDAR_FILTERS.PRICE_ANY,
          // @ts-expect-error: Incorrect type
          label: this.intl.t('market_insight.calendar.any_opportunity'),
        },
        {
          id: CALENDAR_FILTERS.PRICE_HIGH,
          // @ts-expect-error: Incorrect type
          label: this.intl.t('market_insight.higher_than_expected'),
        },
        {
          id: CALENDAR_FILTERS.PRICE_LOW,
          // @ts-expect-error: Incorrect type
          label: this.intl.t('market_insight.lower_than_expected'),
        },
      ],
    },
  ];

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get allCalendarViewFilters() {
    const filters = [];

    if (this.args.isMarketInsightFull) {
      filters.push({
        groupName: this.intl.t('market_insight.opportunities'),
        options: [
          {
            id: CALENDAR_FILTERS.PRICE_HIGH,
            name: this.intl.t('market_insight.price.high'),
          },
          {
            id: CALENDAR_FILTERS.PRICE_LOW,
            name: this.intl.t('market_insight.price.low'),
          },
        ],
      });
    }

    filters.push({
      groupName: this.intl.t('general.daysago', { count: 7 }),
      options: [
        {
          id: CALENDAR_FILTERS.DEMAND_INCREASE,
          name: this.intl.t('market_insight.demand.increase'),
        },
        {
          id: CALENDAR_FILTERS.DEMAND_DECREASE,
          name: this.intl.t('market_insight.demand.decrease'),
        },
      ],
    });

    return filters;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get demandCalendarMonths() {
    const today = dayjs();
    const monthsArray = [
      today,
      today.add(1, 'M'),
      today.add(2, 'M'),
      today.add(3, 'M'),
      today.add(4, 'M'),
      today.add(5, 'M'),
      today.add(6, 'M'),
      today.add(7, 'M'),
      today.add(8, 'M'),
      today.add(9, 'M'),
      today.add(10, 'M'),
      today.add(11, 'M'),
    ];

    const { maxAllowedDay } = this.miSelections;

    if (
      maxAllowedDay &&
      maxAllowedDay.isAfter(monthsArray[monthsArray.length - 1], 'month')
    ) {
      monthsArray.push(today.add(12, 'M'));
    }

    return monthsArray;
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get demandChangeCalendarFilters() {
    return [
      {
        groupName: `${this.intl.t('market_insight.demand')} ${this.intl.t(
          'general.daysago',
          {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            count: this.args.selectedRefDaysAgo.id,
          },
        )}`,
        options: [
          {
            id: CALENDAR_FILTERS.DEMAND_ANY,
            label: this.intl.t('market_insight.calendar.any_demand_change'),
          },
          {
            id: CALENDAR_FILTERS.DEMAND_INCREASE,
            label: this.intl.t('market_insight.demand.increasing'),
          },
          {
            id: CALENDAR_FILTERS.DEMAND_DECREASE,
            label: this.intl.t('market_insight.demand.decreasing'),
          },
        ],
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get monthFormat() {
    return this.dates.partialDateFormat?.YEAR_AND_MONTH_LONG;
  }

  // For the X opportunities to discover message in the main sidebar we need to estimate how much calendar months are visible on screen right now
  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numberOfCalendarMonthsVisibleOnScreen() {
    if (
      this.args.isMarketInsightFull &&
      this.args.screenHeight &&
      // @ts-expect-error: Incorrect type
      this.args.screenHeight - 120 > 0
    ) {
      // We take the window.innerHeight and subtract the 2 main header bars - calendar filter part - the height of actual opportunities message container
      // @ts-expect-error: Incorrect type
      const availableCalendarHeight = this.args.screenHeight - 120 - 50 - 65;

      // This is the height taken by one calendar. It's a bit of an in between value
      // Months can have 4, 5 or 6 rows of weeks. The height can vary from 193px (only february), 226px to 259px.
      // There is also a margin top between months we don't take into account, but this seems to get us close enough

      // In order to do this right we'd need to check the number of weeks in a month, or have the same height for every calendar in the bar.
      // Ain't nobody got time for those solutions though
      const calendarHeight = 235;

      return Math.floor(availableCalendarHeight / calendarHeight);
    }

    // For safety / hiding the message. In case of weird values rather show nothing 🤷🏼‍♂️
    return 0;
  }

  @computed(
    'args.{model.calendar.isLoading,isMarketInsightFull}',
    'numberOfCalendarMonthsVisibleOnScreen',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numberOfOpportunitiesStillToDiscover() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.args.isMarketInsightFull && !this.args.model.calendar.isLoading) {
      const allOutliers = this.store
        .peekAll('outlier')
        // @ts-expect-error: Incorrect type
        .filter((outlier) => !outlier.isDiscarded);

      const opportunitiesStillToDiscover = allOutliers.filter((outlier) => {
        // We check of the day the outlier is attached to comes after the months that are currently visible on the screen
        if (
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          outlier.day.date.isSameOrAfter(
            dayjs().add(this.numberOfCalendarMonthsVisibleOnScreen, 'M'),
            'month',
          )
        ) {
          return true;
        }

        return false;
      });

      return opportunitiesStillToDiscover.length;
    }

    return 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedDemandChangeCalendarCalendarViewFilter() {
    const selectedDemandChangeCalendarViewFilter =
      // @ts-expect-error: Incorrect type
      this.demandChangeCalendarFilters[0].options.find((option) =>
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.args.selectedCalendarViewFilterIds.includes(option.id),
      );

    if (selectedDemandChangeCalendarViewFilter) {
      return selectedDemandChangeCalendarViewFilter;
    }

    // Select the Any opportunities option in case there is no selectedOpportunityCalendarViewFilter
    // @ts-expect-error: Incorrect type
    return this.demandChangeCalendarFilters[0].options[0];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedOpportunityCalendarCalendarViewFilter() {
    const selectedOpportunityCalendarViewFilter =
      // @ts-expect-error: Incorrect type
      this.opportunityCalendarFilters[0].options.find((option) =>
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.args.selectedCalendarViewFilterIds.includes(option.id),
      );

    if (selectedOpportunityCalendarViewFilter) {
      return selectedOpportunityCalendarViewFilter;
    }

    // Select the Any opportunities option in case there is no selectedOpportunityCalendarViewFilter
    // @ts-expect-error: Incorrect type
    return this.opportunityCalendarFilters[0].options[0];
  }

  @action
  // @ts-expect-error: Incorrect type
  demandChangeCalendarSelectionChanged(selection): void {
    const CHANGE_TYPES = {
      demand_increase: 'increase',
      demand_decrease: 'decrease',
      demand_any: 'any',
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (selection.id in CHANGE_TYPES) {
      this.usageAnalytics.track('Demand Change Highlighted', {
        eventTrigger: 'calendar-sidebar',
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        changeType: CHANGE_TYPES[selection.id],
      });
    }

    // @ts-expect-error: Incorrect type
    this.args.setSelectedCalendarViewFilterIds(selection);
  }

  <template>
    <div class={{concat styles.sidebar " " "flex flex-col"}}>
      <div
        class={{concat
          styles.filter-dropdown
          " "
          "flex flex-row items-center gap-500 pt-300 pb-300"
        }}
      >
        <PrismSelectInput
          @isSmall={{true}}
          @onChange={{this.demandChangeCalendarSelectionChanged}}
          @options={{this.demandChangeCalendarFilters}}
          @value={{this.selectedDemandChangeCalendarCalendarViewFilter}}
          data-test-id="sidebar-calendar-demand-button"
        />
        {{#if @isMarketInsightFull}}
          <PrismSelectInput
            @isSmall={{true}}
            {{! @glint-expect-error: Incorrect type }}
            @onChange={{@setSelectedCalendarViewFilterIds}}
            @options={{this.opportunityCalendarFilters}}
            @value={{this.selectedOpportunityCalendarCalendarViewFilter}}
            data-test-id="sidebar-calendar-price-button"
          />
        {{/if}}
      </div>

      <div
        class={{styles.scrollable-content}}
        {{! @glint-expect-error: Incorrect type }}
        id={{@mainSidebarWrapperId}}
        {{! @glint-expect-error: Incorrect type }}
        {{didInsert @initPerfectScrollbarMainSidebar}}
      >
        <div class={{concat styles.calendars " " "flex flex-col"}}>
          {{! @glint-expect-error: Incorrect type }}
          {{#unless @model.calendar.isLoading}}
            {{#each this.demandCalendarMonths as |calendarMonth|}}
              <div
                class={{styles.calendar-scroll-into-view}}
                {{scrollIntoView
                  shouldScroll=(hasSameMonthAndYear
                    calendarMonth @selectedStartDate
                  )
                }}
              >
                <div
                  class={{concat
                    styles.calendar-container
                    " "
                    "flex items-center justify-center"
                  }}
                >
                  <PowerCalendar
                    {{! @glint-expect-error: Incorrect type }}
                    @center={{calendarMonth}}
                    @daysComponent={{HotelMarketInsightCalendarsDaysDayMarketDemand}}
                    {{! @glint-expect-error: Incorrect type }}
                    @onSelect={{@updateSelectedDay}}
                    class={{styles.calendar}}
                    as |calendar|
                  >
                    <calendar.Nav>
                      <h4 class="mb-300">
                        {{powerCalendarFormatDate
                          calendar.center
                          this.monthFormat
                          locale=calendar.locale
                        }}
                      </h4>
                    </calendar.Nav>
                    <calendar.Days
                      {{! @glint-expect-error: Incorrect type }}
                      @isMarketInsightFull={{@isMarketInsightFull}}
                      @maxDate={{dayjsToDate this.miSelections.maxAllowedDay}}
                      @minDate={{dayjsToDate this.miSelections.minAllowedDay}}
                      {{! @glint-expect-error: Incorrect type }}
                      @refDaysAgo={{@selectedRefDaysAgo.id}}
                      @selectedCalendarViewFilterIds={{@selectedCalendarViewFilterIds}}
                      @showDaysAround={{false}}
                      @sidebarCalendarRange={{@sidebarCalendarRange}}
                      @startOfWeek={{this.dates.startOfWeek}}
                      @weekdayFormat="min"
                    />
                  </PowerCalendar>
                </div>
              </div>
            {{/each}}
          {{/unless}}
        </div>
      </div>

      {{#if
        (and
          @isMarketInsightFull
          this.numberOfCalendarMonthsVisibleOnScreen
          this.numberOfOpportunitiesStillToDiscover
        )
      }}
        <div
          class={{concat
            (local
              styles
              "footer"
              (if @hasReachedMainSidebarScrollThreshold "slide-out" "slide-in")
            )
            " "
            "flex items-center justify-center"
          }}
        >
          <p class={{styles.footer-description}}>
            {{t
              "market_insight.more_opportunities_to_discover"
              count=this.numberOfOpportunitiesStillToDiscover
            }}
          </p>
        </div>
      {{/if}}
    </div>
  </template>
}
